import { createFileRoute } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { canonicalLink, hreflangLinks } from "@/lib/seo";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ChangeEvent } from "react";
import { Mail, Phone, MapPin, Send, Plus, Sparkles, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";



import logoEmons from "@/assets/logos/emons.png.asset.json";
import logoFiege from "@/assets/logos/fiege.png.asset.json";
import logoHartmann from "@/assets/logos/hartmann.png.asset.json";
import logoHeinzHuber from "@/assets/logos/heinz-huber.png.asset.json";
import logoHensel from "@/assets/logos/hensel.png.asset.json";
import logoIgsSchreiner from "@/assets/logos/igs-schreiner.png.asset.json";
import logoOffergeld from "@/assets/logos/offergeld.png.asset.json";
import logoVsLogistics from "@/assets/logos/vs-logistics.png.asset.json";
import logoWolfSpedition from "@/assets/logos/wolf-spedition.png.asset.json";
import logoWero from "@/assets/logos/wero.png.asset.json";
import logoRth from "@/assets/logos/rth.png.asset.json";
import logoBuratti from "@/assets/logos/buratti-logo.svg.asset.json";
import logoGebruederMayer from "@/assets/logos/gebrueder-mayer.png.asset.json";
import logoSchwenkLogistik from "@/assets/logos/schwenk-logistik.png.asset.json";

const customerLogos = [
  { name: "Hartmann International", src: logoHartmann.url },
  { name: "Emons", src: logoEmons.url },
  { name: "Wero", src: logoWero.url },
  { name: "Offergeld Logistik", src: logoOffergeld.url },
  { name: "IGS Schreiner", src: logoIgsSchreiner.url },
  { name: "Buratti", src: logoBuratti.url },
  { name: "Fiege", src: logoFiege.url },
  { name: "Heinz Huber", src: logoHeinzHuber.url },
  { name: "Gebrüder Mayer", src: logoGebruederMayer.url },
  { name: "Wolf Spedition", src: logoWolfSpedition.url },
  { name: "Hensel Logistik", src: logoHensel.url },
  { name: "T&H", src: logoRth.url },
  { name: "VS Logistics", src: logoVsLogistics.url },
  { name: "Schwenk Logistik", src: logoSchwenkLogistik.url },
  
];

const faqItems = [
  {
    question: "Was erwartet mich im Erstgespräch?",
    answer:
      "In einem 60-minütigen Gespräch analysieren wir gemeinsam Ihre aktuelle Dispositionssituation. Wir identifizieren Ihre größten Optimierungshebel und zeigen Ihnen, wie Smartlane konkret in Ihrer Disposition eingesetzt werden kann – individuell und auf Augenhöhe.",
  },
  {
    question: "Welche Informationen sollte ich vorbereiten?",
    answer:
      "Idealerweise haben Sie einen groben Überblick über Ihre Dispositionsprozesse, Anforderungen und die wichtigsten Herausforderungen in Ihrer Disposition. Aber keine Sorge – auch ohne Vorbereitung können wir Ihnen einen wertvollen ersten Einblick geben.",
  },
  {
    question: "Bekomme ich eine Live-Demo der Software?",
    answer:
      "Ja, im Erstgespräch zeigen wir Ihnen in einer persönlichen Demo, wie unsere Lösung funktioniert, welche Daten verarbeitet werden und wie die finale Tourenplanung aussieht. Anhand realer Szenarien sehen Sie transparent, wie Smartlane Transport Intelligence arbeitet.",
  },
  {
    question: "Wie schnell kann Smartlane in meinem Unternehmen eingesetzt werden?",
    answer:
      "Wir sind jederzeit startklar! Unser Consulting-Team begleitet Sie von der Datenanbindung bis zum produktiven Einsatz – inklusive Schulung Ihres Teams.",
  },
  {
    question: "Erhalte ich eine individuelle ROI-Berechnung?",
    answer:
      "Im Rahmen eines kostenfreien Proof of Concepts können wir Ihren Anwendungsfall mit realen Daten evaluieren – von einer besseren Auslastung über Zeitersparnis bis zur Fahrzeug-Reduktion. So sehen Sie vorab den wirtschaftlichen Mehrwert.",
  },
];

const sourceOptions = [
  { value: "", label: "Bitte wählen..." },
  { value: "presse", label: "Presse" },
  { value: "veranstaltung", label: "Veranstaltung / Messe" },
  { value: "online-suche", label: "Online Suche" },
  { value: "netzwerk", label: "Netzwerk" },
  { value: "social-media", label: "Social Media" },
  { value: "sonstiges", label: "Sonstiges" },
];

export const Route = createFileRoute("/kontakt")({
  head: () => ({
    meta: [
      { title: "Kontakt – Smartlane GmbH München" },
      {
        name: "description",
        content:
          "Sprechen Sie mit dem Smartlane-Team über KI-Tourenplanung, Demos und individuelle Anforderungen. Schnell, persönlich und unverbindlich.",
      },
      { property: "og:title", content: "Kontakt – Smartlane GmbH München" },
      {
        property: "og:description",
        content: "Direkt zur Smartlane-Demo. Wir melden uns innerhalb eines Werktags.",
      },
      { property: "og:url", content: absoluteUrl("/kontakt") },
      { property: "og:type", content: "website" },
    ],
    links: [canonicalLink("/kontakt"), ...hreflangLinks("/kontakt")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Smartlane Kontakt",
          description:
            "Kontaktieren Sie Smartlane für eine individuelle Beratung zur KI-gestützten Tourenplanung.",
          url: absoluteUrl("/kontakt"),
          inLanguage: "de-DE",
          mainEntity: {
            "@type": "Organization",
            name: "Smartlane GmbH",
            email: "info@smartlane.ai",
            telephone: "+49-89-90411930",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Gotzinger Straße 8",
              addressLocality: "München",
              postalCode: "81371",
              addressCountry: "DE",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${absoluteUrl("/")}#organization`,
          name: "Smartlane GmbH",
          description:
            "Anbieter von KI-basierter Tourenplanungs- und Dispositionssoftware für Speditionen und Logistikdienstleister.",
          url: absoluteUrl("/"),
          email: "info@smartlane.ai",
          telephone: "+49-89-90411930",
          image: absoluteUrl("/favicon.svg"),
          address: {
            "@type": "PostalAddress",
            streetAddress: "Gotzinger Straße 8",
            addressLocality: "München",
            postalCode: "81371",
            addressRegion: "Bayern",
            addressCountry: "DE",
          },
          areaServed: ["DE", "AT", "CH"],
          sameAs: ["https://www.linkedin.com/company/smartlane-gmbh"],
        }),
      },
    ],

  }),
  component: KontaktPage,
});

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqItems.map((item, index) => (
        <div
          key={item.question}
          className="overflow-hidden rounded-2xl border border-border/30 bg-white px-6"
        >
          <button
            type="button"
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="group flex w-full items-center justify-between py-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="font-heading text-lg md:text-xl text-rich-black pr-8">
              {item.question}
            </span>
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                openIndex === index
                  ? "rotate-45 bg-smartlane-orange text-white"
                  : "bg-smartlane-orange/10 text-smartlane-orange group-hover:bg-smartlane-orange/20"
              }`}
            >
              <Plus className="h-5 w-5" />
            </span>
          </button>
          <motion.div
            initial={false}
            animate={{
              height: openIndex === index ? "auto" : 0,
              opacity: openIndex === index ? 1 : 0,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="font-body text-dark-gray leading-relaxed pb-6 pr-16">
              {item.answer}
            </p>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

function KontaktPage() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    fleet: "",
    email: "",
    phone: "",
    message: "",
    source: "",
    privacy: false,
    newsletter: false,
    quiz: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [quizQuestion, setQuizQuestion] = useState("Wird geladen…");
  const [quizToken, setQuizToken] = useState("");
  const [quizLoading, setQuizLoading] = useState(false);
  const loadedAtRef = useRef<number>(Date.now());

  const fetchQuiz = useCallback(async () => {
    setQuizLoading(true);
    try {
      const res = await fetch("/api/public/verify-contact", { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { question: string; token: string };
      setQuizQuestion(data.question);
      setQuizToken(data.token);
      loadedAtRef.current = Date.now();
    } catch (e) {
      console.error("Quiz laden fehlgeschlagen:", e);
      setQuizQuestion("Sicherheitsfrage konnte nicht geladen werden.");
    } finally {
      setQuizLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQuiz();
  }, [fetchQuiz]);

  function onChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    if (form.website) return;
    if (!form.privacy) {
      const msg = "Bitte stimmen Sie der Datenverarbeitung zu.";
      setErrorMsg(msg);
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/verify-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          company: form.company,
          fleet: form.fleet,
          email: form.email,
          phone: form.phone,
          message: form.message,
          source: form.source,
          privacy: form.privacy,
          newsletter: form.newsletter,
          quizAnswer: form.quiz,
          token: quizToken,
          website: form.website,
          loadedAt: loadedAtRef.current,
          sourcePage:
            typeof window !== "undefined"
              ? window.location.href
              : "smartlane.ai/kontakt",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; ok?: boolean };
      if (!res.ok || !data.ok) {
        const msg = data.error ?? "Beim Senden ist ein Fehler aufgetreten.";
        setErrorMsg(msg);
        toast.error(msg);
        await fetchQuiz();
        setForm((p) => ({ ...p, quiz: "" }));
        return;
      }
      trackEvent("kontaktformular_abgesendet", { form_location: "kontakt" });
      setSubmitted(true);
      toast.success("Vielen Dank! Wir melden uns innerhalb eines Werktags.");
    } catch (err) {
      console.error("Submit error:", err);
      const msg =
        "Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt an info@smartlane.ai.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }


  return (
    <div className="overflow-x-hidden bg-white">
      {/* HERO — Split Layout */}
      <section className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-32">
        <div
          aria-hidden
          className="absolute top-20 right-0 h-[600px] w-[600px] rounded-full opacity-[0.07] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, hsl(19 88% 55%) 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden
          className="absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full opacity-[0.04] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, hsl(19 88% 55%) 0%, transparent 70%)",
          }}
        />

        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
            {/* Left — Content */}
            <div className="lg:order-1">
              <h1 className="font-heading text-display-lg xl:text-display-xl uppercase tracking-wider text-rich-black mb-6 text-balance leading-[0.9]">
                Jetzt anfragen und{" "}
                <span className="text-smartlane-orange">
                  Erstgespräch vereinbaren
                </span>
                .
              </h1>

              <p className="font-body text-lg text-dark-gray max-w-xl mb-10 leading-relaxed">
                Von manuellen Prozessen zur intelligenten Kontrolle – wir
                begleiten Sie auf dem Weg zur optimierten Tourenplanung. Füllen
                Sie einfach das Formular aus und wir werden uns
                schnellstmöglich bei Ihnen melden.
              </p>

              <div className="space-y-5">
                <a
                  href="mailto:info@smartlane.ai"
                  className="flex items-center gap-4 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-smartlane-orange/10 transition-colors group-hover:bg-smartlane-orange/20">
                    <Mail className="h-5 w-5 text-smartlane-orange" />
                  </div>
                  <div>
                    <p className="font-body text-xs uppercase tracking-wider text-dark-gray">
                      E-Mail
                    </p>
                    <p className="font-body font-medium text-rich-black">
                      info@smartlane.ai
                    </p>
                  </div>
                </a>

                <a
                  href="tel:+498990411930"
                  className="flex items-center gap-4 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-smartlane-orange/10 transition-colors group-hover:bg-smartlane-orange/20">
                    <Phone className="h-5 w-5 text-smartlane-orange" />
                  </div>
                  <div>
                    <p className="font-body text-xs uppercase tracking-wider text-dark-gray">
                      Telefon
                    </p>
                    <p className="font-body font-medium text-rich-black">
                      +49 89 90411930
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-smartlane-orange/10">
                    <MapPin className="h-5 w-5 text-smartlane-orange" />
                  </div>
                  <div>
                    <p className="font-body text-xs uppercase tracking-wider text-dark-gray">
                      Adresse
                    </p>
                    <p className="font-body font-medium text-rich-black">
                      Gotzinger Straße 8, 81371 München
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Form Card */}
            <div className="lg:order-2" id="kontakt-form">
              <div className="rounded-3xl border border-border/30 bg-white p-8 md:p-10 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)]">
                {submitted ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-smartlane-orange/10">
                      <Sparkles className="h-10 w-10 text-smartlane-orange" />
                    </div>
                    <h3 className="font-heading text-3xl uppercase tracking-wider text-rich-black mb-3">
                      Vielen Dank!
                    </h3>
                    <p className="font-body text-dark-gray">
                      Wir haben Ihre Nachricht erhalten und melden uns zeitnah
                      bei Ihnen.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5">
                    {/* Honeypot */}
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        left: -10000,
                        width: 1,
                        height: 1,
                        overflow: "hidden",
                      }}
                    >
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={onChange}
                      />
                    </div>

                    <h2 className="font-heading text-2xl uppercase tracking-wider text-rich-black mb-2">
                      Nachricht senden
                    </h2>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field
                        label="Name *"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        required
                        placeholder="Max Mustermann"
                      />
                      <Field
                        label="Firmenname *"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        required
                        placeholder="Muster GmbH"
                      />
                    </div>

                    <Field
                      label="Größe Fuhrpark *"
                      name="fleet"
                      value={form.fleet}
                      onChange={onChange}
                      required
                      placeholder="z.B. 50 Fahrzeuge"
                    />

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field
                        label="E-Mail *"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        required
                        placeholder="name@firma.de"
                      />
                      <Field
                        label="Telefonnummer"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+49 ..."
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="mb-1.5 block font-body text-sm text-dark-gray"
                      >
                        Ihre Nachricht *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={form.message}
                        onChange={onChange}
                        placeholder="Beschreiben Sie Ihr Anliegen"
                        className="w-full resize-none rounded-xl border border-border/60 bg-white px-4 py-3 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="source"
                        className="mb-1.5 block font-body text-sm text-dark-gray"
                      >
                        Wie sind Sie auf uns aufmerksam geworden?
                      </label>
                      <select
                        id="source"
                        name="source"
                        value={form.source}
                        onChange={onChange}
                        className="h-12 w-full appearance-none rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
                      >
                        {sourceOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Sicherheitsfrage (HMAC-signiert, 15 Min gültig) */}
                    <div>
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <label
                          htmlFor="quiz"
                          className="block font-body text-sm text-dark-gray"
                        >
                          Sicherheitsfrage * — {quizQuestion}
                        </label>
                        <button
                          type="button"
                          onClick={() => void fetchQuiz()}
                          disabled={quizLoading}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-dark-gray transition hover:text-smartlane-orange disabled:opacity-50"
                          aria-label="Neue Sicherheitsfrage laden"
                        >
                          <RefreshCw className={`h-3 w-3 ${quizLoading ? "animate-spin" : ""}`} />
                          Neu
                        </button>
                      </div>
                      <input
                        id="quiz"
                        name="quiz"
                        type="text"
                        inputMode="numeric"
                        required
                        value={form.quiz}
                        onChange={onChange}
                        placeholder="Ihre Antwort"
                        className="h-12 w-full rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
                      />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="privacy"
                        checked={form.privacy}
                        onChange={onChange}
                        className="mt-1 h-5 w-5 rounded border-border/60 accent-smartlane-orange focus:ring-smartlane-orange/30"
                      />
                      <span className="font-body text-sm leading-relaxed text-dark-gray">
                        * Ich stimme der Datenverarbeitung zu. Weitere
                        Informationen finden Sie in der{" "}
                        <a
                          href="/datenschutz"
                          className="text-smartlane-orange hover:underline"
                        >
                          Datenschutzerklärung
                        </a>
                        .
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="newsletter"
                        checked={form.newsletter}
                        onChange={onChange}
                        className="mt-1 h-5 w-5 rounded border-border/60 accent-smartlane-orange focus:ring-smartlane-orange/30"
                      />
                      <span className="font-body text-sm leading-relaxed text-dark-gray">
                        Ich möchte zusätzlich den Smartlane-Newsletter abonnieren.
                      </span>
                    </label>

                    {errorMsg && (
                      <p
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 font-body text-sm text-red-700"
                      >
                        {errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-smartlane-orange font-body text-sm font-semibold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.01] hover:bg-smartlane-orange-glow active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
                    >
                      <Send className="h-4 w-4" />
                      {submitting ? "Wird gesendet…" : "Nachricht senden"}
                    </button>
                  </form>

                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY TALK TO US — FAQ */}
      <section className="bg-light-gray py-24 md:py-36">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <p className="mb-4 font-body text-sm font-medium uppercase tracking-[0.2em] text-smartlane-orange">
                WARUM MIT UNS SPRECHEN?
              </p>
              <h2 className="font-heading text-display-sm md:text-display-md uppercase tracking-wider text-rich-black mb-6">
                60 Minuten für einen{" "}
                <span className="text-smartlane-orange">klaren Fahrplan</span>.
              </h2>
              <p className="font-body text-lg leading-relaxed text-dark-gray">
                Sie haben Fragen zu unserem Erstgespräch und der Live-Demo?
                Hier erfahren Sie, was Sie erwartet – damit Sie den Mehrwert
                Ihrer investierten Zeit klar erkennen können.
              </p>
            </div>
            <FAQAccordion />
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF — Logo Slider */}
      <section className="overflow-hidden bg-white py-16 md:py-24">
        <div className="container-wide mb-10">
          <div className="text-center">
            <p className="mb-4 font-body text-sm font-medium uppercase tracking-[0.2em] text-smartlane-orange">
              VERTRAUEN FÜHRENDER LOGISTIKUNTERNEHMEN
            </p>
            <h2 className="font-heading text-display-sm md:text-display-md uppercase tracking-wider text-rich-black">
              Setzen auch Sie auf{" "}
              <span className="text-smartlane-orange">Smartlane</span>
            </h2>
          </div>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-20 bg-gradient-to-r from-white to-transparent md:w-32" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-20 bg-gradient-to-l from-white to-transparent md:w-32" />

          <div
            className="flex items-center gap-16 md:gap-24 animate-logo-scroll"
            style={{ width: "max-content" }}
          >
            {[...customerLogos, ...customerLogos].map((logo, index) => (
              <div
                key={`${logo.name}-${index}`}
                className="group flex h-10 shrink-0 items-center justify-center md:h-12"
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  loading="lazy"
                  className="h-full w-auto max-w-[120px] object-contain opacity-40 grayscale transition-all duration-500 group-hover:opacity-100 group-hover:grayscale-0 md:max-w-[140px]"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block font-body text-sm text-dark-gray"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
      />
    </div>
  );
}
